// ============================================================================
// ui/quote-summary.js — what the buyer already told us, read back rather than re-asked
// ============================================================================
// Ryan 2026-08-05, auditing the Request Quote tab: *"don't require a second logo get
// uploaded. this has already been done by the point they get to this tab, or if it
// wasn't done, it means they're going with a logoless design which is fine. Same with
// 'official colors' (which we can just call colors). These have already been taken care
// of so can get pulled across."*
//
// So this module owns exactly one idea: **the quote form asks for nothing the design
// already knows.** Three things are derived here rather than collected —
//
//   the SET      which products they picked on the Pick Weights tab
//   the ARTWORK  what they uploaded on Add Logo, and whether it is already print-ready
//   the COLOURS  the palette they chose on Customize Design
//
// Each returns a plain data object; the tab renders it and the payload carries it. Kept
// out of product-step.js because all three are also what a REP needs to read in the lead,
// so the same derivation feeds the form, the handoff and (later) the design sheet.
// ============================================================================

import { CATEGORIES, productKey, baseProductKey, designLadder, productSupportsBodyColor, defaultWeightFor } from '../products.js';
import { displayWeightText, resolvePaletteRef, normalizeHex, plateColorLabel, colorWord, DEFAULT_BODY_COLOR } from '../design-profile.js';

/** Dumbbells ship as PAIRS (Ryan 2026-08-05: *"everything can be ordered in whatever
 *  quantity — but dumbbells are sold as pairs, so these can be any quantity of pairs"*).
 *  Everything else is counted as singles. The unit is a property of the CATEGORY, so it
 *  is stated once here rather than re-decided at each place that renders a quantity. */
export const PAIRED_CATEGORIES = Object.freeze(new Set(['dumbbell']));

/** @returns {'pair'|'each'} */
export function quantityUnit(category) {
  return PAIRED_CATEGORIES.has(category) ? 'pair' : 'each';
}

/**
 * THE RACKS PEOPLE ACTUALLY BUY (Ryan 2026-08-05: *"we need killer UX/UI… Easy buttons like 'Add
 * 5-50 lb Set' or 'Add 5-100 lb Set' or 5-125, 5-75, 5-150"* — and *"add easy buttons for entire
 * 20-110 lb sets of the fixed barbells"*).
 *
 * A long ladder is almost never filled one box at a time: a gym buys a RACK, described by where it
 * starts and stops. One click per named rack is the whole interaction.
 *
 * ⚠ KEYED BY CATEGORY, because the rack's shape is a property of the product. Dumbbells run from 5
 * in fives with several sensible stopping points; the fixed-barbell house rack is one thing — all
 * ten bars, 20 through 110 — so it gets one button rather than a menu of arbitrary sub-racks.
 * A category absent from here simply gets no set buttons; its ladder is short enough to fill by hand.
 *
 * ⚠ `step` IS WHAT KEEPS A NAMED RACK HONEST. The rubber dumbbell ladder also carries 7.5 / 12.5 /
 * 17.5 / 22.5 / 27.5, a deliberate light-end extra rather than part of "a 5–50 set" — filling those
 * silently would put five pairs nobody asked for into a quote.
 *   ⚠ THEY NOW HAVE A RACK OF THEIR OWN (Ryan 2026-08-07), and that does NOT reverse the rule above.
 * The objection was to filling them SILENTLY, inside a button that says "5–50"; a button that names
 * them — "Add 7.5–27.5 lb set" — is the opposite of that. `step` still keeps every OTHER rack clean.
 *
 * ⚠ A CATEGORY NOW HOLDS A LIST OF RACK SHAPES, not one. Half-pound rungs cannot be expressed as a
 * stop on the 5-from-5 rack: they are a different ladder that happens to share a product. Each entry
 * carries its own `from`/`step`, and `stops` are unique across the list so a `top` still identifies
 * exactly one rack.
 */
export const QUANTITY_SETS = Object.freeze({
  dumbbell: Object.freeze([
    Object.freeze({ from: 5, step: 5, stops: Object.freeze([50, 75, 100, 125, 150]) }),
    // The light-end half-pound rack. Only the round rubber dumbbells carry these rungs; every other
    // kind is filtered out automatically by the `length > 1` test in `setsFor`, so nothing here has
    // to name a product.
    Object.freeze({ from: 7.5, step: 5, stops: Object.freeze([27.5]) }),
  ]),
  barbell: Object.freeze([
    Object.freeze({ from: 20, step: 10, stops: Object.freeze([110]) }),
  ]),
});

/** Every rack shape defined for this product's category. */
function specsFor(item) {
  return QUANTITY_SETS[item.category] || [];
}

/** The rack a given `top` belongs to — tops are unique across a category's shapes. */
function specForTop(item, top) {
  return specsFor(item).find((sp) => sp.stops.includes(top)) || null;
}

/** The named racks offered for this product, as `{from, top}` pairs. Empty when it has none. */
export function setsFor(item) {
  return specsFor(item)
    .flatMap((sp) => sp.stops.map((top) => ({ from: sp.from, top })))
    .filter((r) => setRungs(item, r.top).length > 1);
}

/** The rungs a named set covers, in the product's own ladder. */
export function setRungs(item, top) {
  const spec = specForTop(item, top);
  if (!spec) return [];
  /* ⚠ THE STEP IS MEASURED FROM `from`, NOT FROM ZERO — and that single change is what lets the two
     dumbbell racks share one ladder without leaking into each other. The old test was
     `Number.isInteger(w) && w % step === 0`, which excluded every half-pound rung globally; there is
     no `%`-from-zero that admits 7.5→27.5 while still rejecting them from the 5–50 rack.
       From-relative, both fall out for free: on the 5-rack, 7.5 gives (7.5−5)%5 = 2.5 → rejected, so
     the integer racks behave EXACTLY as before; on the 7.5-rack, 12.5 gives (12.5−7.5)%5 = 0 →
     kept. `Number.isInteger` is therefore redundant, not relaxed.
       Float-safe because every rung is a multiple of 0.5 and so exactly representable in binary —
     these subtractions carry no rounding error. A ladder in thirds would need a scaled comparison. */
  return (item.weights || []).filter(
    (w) => w >= spec.from && w <= top && (w - spec.from) % spec.step === 0,
  );
}

/**
 * THE SET BEING QUOTED — the products picked on the first tab, each with its real
 * denomination ladder.
 *
 * ⚠ NOT a fresh question. The old tab re-asked category + kind here with its own picker,
 * which was both a re-ask and a trap: choosing a kind called `switchProduct`, so a buyer
 * could silently change the product they had just spent ten minutes designing. It also
 * captured only ONE product, while the Pick Weights tab lets them choose any combination
 * of four categories — so a buyer who designed a three-product set quoted one of them.
 *
 * Falls back to the product on the stage when nothing was explicitly picked (a resumed
 * share link, or a gate skipped), because a quote for the plate they are looking at is
 * always better than a quote for nothing.
 */
/**
 * The plate colour a given CONFIGURATION is wearing.
 *
 * ⚠ READ HERE RATHER THAN IMPORTED FROM `ui/state.js`, which already has `bodyColorFor`. This
 * module is the quote's pure summariser — every test in `quote-summary.smoke.mjs` builds a bare
 * `{ui, designProfile}` object rather than a real state, and pulling in the state module would
 * drag the whole mutation core (and its imports of layouts + the render engine) into a file whose
 * entire job is to describe. The rule it applies is three lines and stated in one place each side.
 */
function plateHexFor(state, key) {
  const p = state.designProfile || {};
  if (!productSupportsBodyColor(key)) return DEFAULT_BODY_COLOR;
  // The LIVE field for the configuration on the stage; the saved map for its siblings. Absence
  // means "inherit the design's colour", which is exactly what an undyed second pick is wearing.
  if (key === state.ui.product) return normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR;
  return normalizeHex(p.savedBodyColor && p.savedBodyColor[key]) || normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR;
}

/** The rung a configuration claims — its own choice, else the one its instance number implies. */
function rungOf(p) {
  const w = Number(p && p.weight);
  if (Number.isFinite(w) && w > 0) return w;
  const n = Math.max(1, Math.trunc(Number(p && p.instance)) || 1);
  const d = Number(defaultWeightFor(productKey(p.category, p.kind, n)));
  return Number.isFinite(d) ? d : 0;
}

export function quotedSet(state) {
  const picks = Array.isArray(state.ui.selectedProducts) ? state.ui.selectedProducts : [];
  const raw = picks.length
    ? picks
    : (state.ui.productCategory && state.ui.productKind
      ? [{ category: state.ui.productCategory, kind: state.ui.productKind }]
      : []);
  /* ⛔ THE FORM SORTS; IT USED TO PRINT CLICK ORDER (Ryan 2026-08-10: *"we need these cards arranged
     by product, lowest weight on top to biggest at the bottom… if someone starts with 10 and 25 comp
     urethane and rubber dumbbells, then adds 45 lb comp urethane, tab 4 breaks them up so we get 10,
     25, dumbbells, 45"*).
       That is exactly what happened and it was never a grouping bug: this walked
     `state.ui.selectedProducts`, which is APPEND-BY-CLICK. `pickedProducts()` in main.js has sorted
     for a year, but it feeds the switcher and the stage — the quote form never went through it.
     ⚠ ASCENDING BY WEIGHT, against the designer's descending. Not an inconsistency: Ryan set this
     surface's direction on 2026-08-02 (*"I want them to always be from lowest to highest in terms of
     number"*, the same instruction `designLadder` follows for the rows INSIDE each card), and set
     the designer's on 2026-08-10. A form is read top to bottom like a list; a switcher opens on the
     headline plate. The two orders match their two jobs.
     ⚠ DERIVED HERE, NOT IMPORTED. `pickedProducts()` lives in main.js and closes over the live
     state; this module is the quote's pure summariser (every test in quote-summary.smoke.mjs builds
     a bare `{ui, designProfile}`), so importing it would drag the mutation core into a file whose
     whole job is to describe. Same comparator shape, one term flipped, stated in both places. */
  const catOrder = CATEGORIES.map((c) => c.id);
  const kindIdx = (p) => {
    const cat = CATEGORIES.find((c) => c.id === p.category);
    const i = cat ? cat.kinds.indexOf(p.kind) : -1;
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  const instOf = (p) => Math.max(1, Math.trunc(Number(p.instance)) || 1);
  const list = [...raw].sort((a, b) => (catOrder.indexOf(a.category) - catOrder.indexOf(b.category))
    || (kindIdx(a) - kindIdx(b))
    || (rungOf(a) - rungOf(b))
    // Instance last so the comparator stays TOTAL: two configurations can sit on one rung, and a
    // tie would otherwise fall through to sort stability, i.e. back to the click order this fixes.
    || (instOf(a) - instOf(b)));
  /* ══ ONE CARD PER APPEARANCE, NOT PER CONFIGURATION (Ryan 2026-08-10) ═══════════════════════════
   * *"Sometimes someone might accidentally or intentionally have 2 identical designs… it probably
   * makes sense to combine them both on the request quote tab into 1 card."*
   *
   * ⭐ TWO CONFIGURATIONS OF ONE PRODUCT IN ONE COLOUR ARE ONE THING TO BUY. The design (logo,
   * layout, fonts, lettering) is shared across configurations by construction — the ONLY thing that
   * can differ is the plate colour. So two yellows are not two products, they are one yellow set the
   * buyer happened to design in two sittings, and nothing on an invoice could tell them apart.
   * Printing two identical blocks invites exactly the double-entry Ryan was worried about, and no
   * warning dialog is needed once the ambiguity cannot be expressed.
   *
   * ⛔ GROUPED GLOBALLY, NOT BY ADJACENCY. The list above is sorted by WEIGHT within a kind, so two
   * yellows are not necessarily neighbours — yellow 10, green 25, yellow 45 interleaves them. A
   * `prev`-comparison merge would silently produce three cards and look right in every test that
   * happened to use two colours in ascending order.
   * ⚠ Insertion order is preserved, so a card lands where its LIGHTEST member sits — which is the
   * lowest-to-highest reading Ryan asked for, applied to the merged card rather than to the picks. */
  const byLook = new Map();
  for (const p of list) {
    const instance = Math.max(1, Math.trunc(Number(p.instance)) || 1);
    const hex = plateHexFor(state, productKey(p.category, p.kind, instance));
    const id = `${p.category}|${p.kind}|${hex}`;
    const card = byLook.get(id);
    if (card) {
      card.instances.push(instance);
      card.designed.push(rungOf(p));
      continue;
    }
    byLook.set(id, { category: p.category, kind: p.kind, hex, instances: [instance], designed: [rungOf(p)] });
  }
  const cards = [...byLook.values()];
  /* Which products still yield MORE THAN ONE CARD — the only ones whose blocks need a colour to be
     told apart. ⚠ Counted over CARDS, not over picks: two identical yellows that merged into one
     card are no longer ambiguous with anything, and naming their colour would be answering a
     question the form no longer asks. */
  const cardCount = new Map();
  for (const c of cards) {
    const k = productKey(c.category, c.kind);
    cardCount.set(k, (cardCount.get(k) || 0) + 1);
  }

  return cards.map(({ category, kind, hex, instances, designed }) => {
    const cat = CATEGORIES.find((c) => c.id === category);
    /* THE INSTANCE RIDES THE KEY, and that one word is what keeps two DIFFERENT bumper sets from
       double-counting. Every quantity on this form is stored at `${key}|${weight}` and every total
       re-reads it from there, so two cards sharing a key would share a box: type 10 pairs of 45s
       against the white set and the green set would report the same 10 — one order, billed and
       manufactured twice.
       ⚠ A MERGED CARD DELIBERATELY COLLAPSES TO ONE LEDGER, which is the same rule read the other
       way: its members are indistinguishable, so they SHOULD share a box, and keeping two would let
       one yellow set be ordered twice. The lowest instance owns it — stable across a rebuild, and it
       is the one that exists in every save (instance 1 is spelt with no suffix). */
    const instance = Math.min(...instances);
    const key = productKey(category, kind, instance);
    return {
      category,
      kind,
      key,
      instance,
      /* ⭐ THE PLATE COLOUR, AND ONLY WHEN IT DISTINGUISHES SOMETHING (Ryan 2026-08-09). A rep
         reading two blocks that both say "Bumper Plates — Competition Urethane" cannot quote the
         order: the colour is the entire difference between them. Null wherever the product yields a
         single card, so the overwhelming majority of quotes read exactly as they always have. */
      plateColor: cardCount.get(productKey(category, kind)) > 1 ? hex : null,
      /* ⛔ THE COLOUR THIS CARD IS, ALWAYS — as distinct from `plateColor`, which is a DISPLAY
         decision ("is there a second card to tell this apart from?") and is deliberately null on a
         single-card product. Anything that has to PAINT the card needs the real value regardless,
         and reading `plateColor` for that put a null through to the renderer, which fell back to the
         profile's live `bodyColor` — i.e. to whichever configuration happened to be on the stage.
         Ryan 2026-08-10: *"I changed one to green… now it shows 2 cards but both show the green SVG
         image instead of one showing the yellow for the 45s and green for the 35s."*
         ⚠ Never null: `plateHexFor` answers DEFAULT_BODY_COLOR for anything that cannot be dyed, so
         one field covers both and no caller has to branch. */
      plateHex: hex,
      /* ⭐ THE RUNGS THIS CARD WAS ACTUALLY DESIGNED ON (Ryan 2026-08-10: *"add the weight
         denomination to bumper cards when more than 1 of the same product has been designed, so that
         IF they intended to order based strictly off of what they designed, it's clear for them to
         see what they designed"*).
         ⚠ A LABEL, NOT A RESTRICTION. Every card still offers the whole ladder — the Customize tab's
         ladder decides which plate you are LOOKING at, never which you may buy, and a buyer who
         designed one yellow 45 plainly wants to order 10s through 55s of it. This only marks which
         of those rungs they have actually seen rendered.
         ⚠ Empty on a single-card product, so nothing new appears on the ordinary one-design quote. */
      designedRungs: cardCount.get(productKey(category, kind)) > 1
        ? [...new Set(designed)].filter((w) => w > 0).sort((a, b) => a - b)
        : [],
      label: cat ? cat.label : category,
      one: cat ? cat.one : category,
      unit: quantityUnit(category),
      /* ⚠ THE SAME LADDER THE CUSTOMIZE TAB SHOWS, from the same function (Ryan 2026-08-05: *"no
         reason to change it up now on the last step and have people be confused"*). It carries the
         lightest-first order AND the fixed barbell's house-rack fallback — without the latter a
         barbell buyer reached this grid with no rows in it at all. */
      weights: designLadder(key) || [],
    };
  });
}

// ── ARTWORK ──────────────────────────────────────────────────────────────────────────
// Vector formats are the ones a printer can use directly. `.pdf` is here because the app
// accepts and rasterises PDF uploads (ui/pdf-to-image.js) — the buyer still HAS the vector,
// which is the only thing this test is about.
const VECTOR_EXT = /\.(svg|eps|ai|pdf)$/i;
const VECTOR_MIME = /(svg|postscript|illustrator|pdf)/i;

/**
 * What artwork is on file, and whether production still needs something better.
 *
 * @returns {{present: boolean, fileName: string|null, isVector: boolean, needsVector: boolean}}
 *   `needsVector` is the ONLY reason the form shows an attach control at all — and it is
 *   never a requirement. A raster logo is quotable; it just means the rep asks for a vector
 *   during proofing, which is a conversation, not a blocker.
 */
export function artworkOnFile(state) {
  const el = (state.designProfile.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el) return { present: false, fileName: null, isVector: false, needsVector: false };
  const logo = el.logo;
  const name = logo.fileName || null;
  const isVector = logo.isVector === true
    || VECTOR_EXT.test(String(name || ''))
    || VECTOR_MIME.test(String(logo.mimeType || ''));
  return { present: true, fileName: name, isVector, needsVector: !isVector };
}

// ── COLOURS ──────────────────────────────────────────────────────────────────────────

const hex = (v) => (typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : null);

/**
 * The palette the design actually wears, as swatches a rep can read.
 *
 * Deliberately derived from the PROFILE rather than from the picker's UI state: the plate
 * body, the fills and the outline stack are what got painted, and they are what a proof has
 * to match. De-duplicated on hex, because one colour used as both a fill and an outline is
 * one colour to match, not two.
 */
export function designColors(state) {
  const p = state.designProfile || {};
  const out = [];
  const push = (role, value) => {
    const h = hex(value);
    if (!h) return;
    const seen = out.find((c) => c.hex === h);
    if (seen) { if (!seen.roles.includes(role)) seen.roles.push(role); return; }
    out.push({ hex: h, roles: [role] });
  };
  push('Plate', p.bodyColor);
  const pal = p.palette || {};
  push('Primary', pal.primary);
  push('Secondary', pal.secondary);
  push('Accent', pal.tertiary);
  // …and anything an element wears directly, which is where a per-piece colour change lands.
  //   ⚠ THROUGH `resolvePaletteRef`. An element stores a palette REF (`'primary'`), not a hex —
  // reading the field raw yields "primary" and silently contributes nothing, so the swatches would
  // have shown only the plate and the palette while a per-piece recolour went unreported.
  for (const e of p.elements || []) {
    if (!e) continue;
    push('Ink', resolvePaletteRef(e.fillRef || 'primary', pal));
    for (const o of e.outlines || []) push('Outline', o && resolvePaletteRef(o.colorRef, pal));
  }
  return out;
}

/**
 * One line per product for the rep — INCLUDING the products with no numbers on them.
 *
 * ⛔ A DESIGN WITH NO QUANTITY IS STILL A LEAD (Ryan 2026-08-05: *"if the customer creates
 * design(s) but leaves quantities at zero, reps still need to find out about it… don't assume that
 * b/c they got to the 1 yard line and forgot to enter quantities on one or more of their designs
 * that they don't need them"*). Someone who designed a plate, filled in their contact details and
 * pressed the button wants that plate. Dropping it from the description because a box was empty
 * would hide the whole product from the rep, which is the one outcome nobody wants.
 *
 * Three states, and the rep is told which is which, because they mean different things:
 *   a count  they said how many
 *   TBD      they said they don't know yet (the explicit button)
 *   blank    they said nothing — most likely missed it, so it is the one to chase first
 */
export function describeQuantities(set, quantities, unit, tbd = {}, mode = null) {
  const lines = [];
  /* THE BUYER'S OWN ANSWER TO "do you know your quantities yet?" comes first, because it changes how
     a rep should read everything under it (Ryan 2026-08-05). "Talk to a rep first" is a stated
     position on a longer-lead project, not a form they failed to finish. */
  if (mode === 'rep') {
    lines.push('⚑ BUYER CHOSE "talk to a rep first" — quantities deliberately not entered yet');
  }
  for (const item of set) {
    /* ⭐ THE COLOUR IS PART OF THE NAME WHEN, AND ONLY WHEN, IT HAS TO BE (Ryan 2026-08-09). Two
       configurations of one competition bumper print the identical "Bumper Plates — Competition
       Urethane", and a rep reading those two lines cannot tell which quantities belong to which
       plate — which is the whole order. `plateColor` is set solely on a product picked more than
       once, so every other quote reads exactly as it always has.
         The HEX rides along with the word deliberately: "green" is an approximate name for a value
       a proof has to match exactly. See `plateColorLabel`. */
    const head = item.plateColor
      ? `${item.label} — ${item.kind}, ${plateColorLabel(item.plateColor)}`
      : `${item.label} — ${item.kind}`;
    if (mode === 'rep') { lines.push(`${head}: wants this, quantity to be worked out with a rep`); continue; }
    if (tbd[item.key]) { lines.push(`${head}: quantity TBD — buyer asked to work it out with a rep`); continue; }
    const rows = (item.weights || [])
      .map((w) => ({ w, n: Number(quantities[`${item.key}|${w}`]) || 0 }))
      .filter((r) => r.n > 0);
    if (!rows.length) { lines.push(`${head}: ⚠ NO QUANTITY GIVEN — designed but left blank, ask`); continue; }
    const parts = rows.map((r) => {
      const label = `${displayWeightText(r.w, unit)} ${unit}`;
      // Pairs are reported BOTH ways. A rep reading "4 × 45 lb" off a dumbbell line would be
      // reading half the rack — the unit is the whole meaning of the number.
      return item.unit === 'pair' ? `${r.n} pair${r.n === 1 ? '' : 's'} of ${label} (${r.n * 2} dumbbells)` : `${r.n} × ${label}`;
    });
    lines.push(`${head}: ${parts.join(', ')}`);
  }
  return lines;
}

/** Total pieces across the whole set, counting a pair as two. For the button's own summary. */
export function totalPieces(set, quantities) {
  let n = 0;
  for (const item of set) {
    for (const w of item.weights || []) {
      const q = Number(quantities[`${item.key}|${w}`]) || 0;
      n += item.unit === 'pair' ? q * 2 : q;
    }
  }
  return n;
}

/**
 * Total POUNDS across the whole set (Ryan 2026-08-07: *"can it also say '10 pieces - 285 lbs'"*).
 *
 * ⚠ ALWAYS POUNDS, whatever the toggle says — the rungs themselves are pounds and the unit is a
 * LABEL on them (design-profile: "UNIT IS A LABEL, NOT A WEIGHT"). Converting for display is the
 * caller's job, exactly as it is for every rung on the form; summing in the displayed unit here
 * would mean rounding thirty times and then adding up the errors.
 *
 * ⚠ AND IT COUNTS A PAIR AS TWO, the same way `totalPieces` does. A dumbbell rung is one PAIR at
 * that denomination, so a 50 lb pair is 100 lb on the floor — reporting 50 would make the one
 * product a gym actually weighs a truck for read light by half. The two functions share the rule by
 * being read together, right here; they are four lines apart for that reason.
 */
export function totalWeightLb(set, quantities) {
  let lb = 0;
  for (const item of set) {
    for (const w of item.weights || []) {
      const q = Number(quantities[`${item.key}|${w}`]) || 0;
      const each = Number(w) || 0;
      lb += item.unit === 'pair' ? q * each * 2 : q * each;
    }
  }
  return lb;
}

/* ── THE MINIMUM ORDER (Ryan 2026-08-30) ──────────────────────────────────────────────────────
 * Two figures, and each is the smallest order of its kind that is worth running: 230 lb is a pair
 * each of 10/25/35/45, and 200 lb is the five-bar fixed-barbell set (20+30+40+50+60). Dumbbells
 * take the lighter figure with the barbells — Ryan's own call, "for simple math".
 *
 * ⛔ ONE THRESHOLD FOR THE WHOLE SET, MEASURED ON THE TOTAL — never a minimum per category. Asked
 * directly about 150 lb of dumbbells beside 150 lb of plates, Ryan chose "highest applicable
 * minimum, on the total" (2026-08-30): per-category floors would refuse that order twice over while
 * it is 300 lb on a truck, which is exactly the freight-efficient shape the floor exists to
 * encourage. So a set containing plates answers 230, anything else answers 200, and
 * `totalWeightLb` is the single number weighed against it. One target on screen, one countdown.
 *
 * ⚠ PURE, AND IT TAKES THE SET rather than reading state — Add to Cart is coming (Ryan 2026-08-30,
 * "design for it now, build it later") and it has to be able to ask this same question without
 * going anywhere near the quote form.
 */
export const MOQ_PLATE_LB = 230;
export const MOQ_BASE_LB = 200;
/** The categories that raise the floor. Both are plates; dumbbells and barbells are the base case. */
const PLATE_CATEGORIES = new Set(['bumper', 'grip']);

export function minimumFor(set) {
  return (set || []).some((item) => PLATE_CATEGORIES.has(item.category)) ? MOQ_PLATE_LB : MOQ_BASE_LB;
}

/**
 * How many POUNDS short of the minimum this set is — 0 once it clears.
 *
 * The gate, the countdown and the blocked-submit message are all this one number, so they cannot
 * disagree about whether the order qualifies. (Pounds, like everything else here: the rungs are
 * pounds and the unit is a label on them. Convert once, at the point of display.)
 */
export function moqShortfall(set, quantities) {
  return Math.max(0, minimumFor(set) - totalWeightLb(set, quantities));
}

/* ── WHICH DENOMINATION A DESIGN IS SHOWN ON (Ryan 2026-08-30) ────────────────────────────────
 * *"If someone only wants to buy 50-100 lb set of dumbbells, when they reopen their link later to
 * see their design, it shouldn't default to 25 lb pair."*
 *
 * ⛔ AND THE RESTORE PATH WAS NEVER BROKEN — this is a DISPLAY rule, not a repair. A reopened link
 * puts back exactly the `weightValue` the sender's stage carried, faithfully. But the plate somebody
 * DESIGNED on and the weights they ORDERED are two independent facts: open the tool, land on the
 * 25 lb rung a fresh dumbbell opens on, do all the artwork there, then go to the quote tab and enter
 * counts for 50s through 100s. The design genuinely IS a 25. Restoring it is correct, and useless.
 *
 * THE RULE, Ryan's own pick of three: keep the design-time plate when it appears among the ordered
 * lines — that is the view they built and expect back — and otherwise move to the line with the
 * highest quantity, breaking ties toward the heavier weight.
 *
 * ⚠ IT ANSWERS `current` UNCHANGED WHENEVER IT CANNOT DO BETTER: no ordered lines for this product,
 * a link written before orders were recorded, a plain share that never went through a quote, a
 * ladder that cannot draw the winning rung. Every caller can therefore apply it unconditionally,
 * which is the only thing that stops it being applied in some of the places a design is displayed
 * and not others — and there are two such places today, the reopened link and the vendor sheet,
 * with the same wrong answer on both.
 *
 * @param {Array} ordered  `[{ product: 'category|kind', unit, lines: [{weight, qty}] }]`
 * @param {string} product The product key on the stage; the instance suffix is stripped, because a
 *                         second yellow 45 is the same THING TO BUY as the first (see `quotedSet`).
 * @param {number} current The weight the design carries now — the answer whenever nothing beats it.
 * @param {Array<number>} [allowed] The rungs this model can actually draw. Supplied by callers that
 *                         have the ladder, because a QUOTABLE rung is not always a DESIGNABLE one
 *                         and picking one the model cannot render would trade a wrong plate for a
 *                         broken one. Omit and every ordered line is eligible.
 */
/**
 * The order, shaped for the rep's Quote Workstation handoff.
 *
 * ⛔ THIS DELIBERATELY CARRIES NO SKU, NO HANDLE AND NO PRICE, and that is the whole design of it.
 * The Weight Designer has no catalogue identity layer anywhere in `app/src` — a product is
 * `category|kind` plus a number of pounds, end to end — and it must stay that way for two reasons.
 * The first is that the house brand's product handles all begin with its NAME, which `brand-flag.js`
 * still gates from buyers; the help build has a tripwire for that leak but it scans the four help
 * outputs only, not this bundle, so a handle table added here would ship the gated name to every
 * visitor with the guard reporting green. The second is simply that the catalogue is not this app's
 * subject. `assets/workstation-boot.js` owns the mapping, next to the `/product-by-handle` call
 * that consumes it.
 *
 * The shape is deliberately the SAME one `/wd-quote` already posts and `/wd-share` reads back as
 * `ordered`, so a reader who understands one understands both, and `orderedDenomination` above can
 * consume either without a translation step.
 *
 * ⚠ A DUMBBELL LINE IS A COUNT OF PAIRS, not of bells. `unit` travels with every group so the
 * receiver never has to infer it from the category, which is the one error on this brand that
 * misquotes a rack by exactly 2x.
 *
 * ⚠ ZERO-QUANTITY RUNGS ARE DROPPED, and a product the buyer picked but never counted therefore
 * produces NO group at all. That is correct (an uncounted product is not an order), but it means an
 * empty result is a real and expected outcome — most obviously in rep mode, where the quantity grid
 * is never shown. The caller must SAY so rather than opening a silently empty quote.
 *
 * @param {Array} set        `quotedSet(state)`
 * @param {Object} quantities `${item.key}|${weight}` -> count
 * @returns {Array<{product:string, key:string, unit:'pair'|'each', label:string, one:string,
 *                  configuration:string|null, lines:Array<{weight:number, qty:number}>}>}
 */
export function orderedForQuote(set, quantities) {
  const q = quantities || {};
  return (set || [])
    .map((item) => ({
      // The BASE key: a second configuration of a plate is the same thing to buy as the first, so
      // the catalogue lookup must not see the `#2`. The instance-bearing key rides along as `key`
      // because the two configurations are different ROWS, told apart by their colour.
      product: baseProductKey(item.key),
      key: item.key,
      unit: item.unit === 'pair' ? 'pair' : 'each',
      label: item.label,
      one: item.one,
      configuration: item.plateColor ? colorWord(item.plateColor) : null,
      /* ⛔ A PRODUCT WITH NO COUNTS SHIPS ITS WHOLE LADDER AT ZERO (Ryan 2026-09-01). He picked a
         dumbbell, entered nothing, chose "Brand new quote" and got an empty Workstation: *"I wanted
         to test if it still imported the dumbbells properly just with 0 quantity for each line item
         which I could then fill out in the QW."*

         The old shape dropped every zero line and then every product left with no lines, so
         "acceptable to leave quantities at 0" produced nothing to leave at 0. Filling counts in the
         Quote Writer is the point: the rows have to exist there to be filled.

         ⚠ PER PRODUCT, NOT PER ORDER. A product the rep DID count ships only what they counted —
         entering three rungs of a 35-rung dumbbell must not also drag in thirty-two zeroes they
         then delete. The fallback is for silence, and silence is per product: count the plates,
         leave the bells blank, and you get exactly the plate rows plus the full bell ladder. */
      lines: (() => {
        const all = (item.weights || [])
          .map((w) => ({ weight: w, qty: Number(q[`${item.key}|${w}`]) || 0 }));
        const counted = all.filter((l) => l.qty > 0);
        return counted.length ? counted : all;
      })(),
    }))
    .filter((g) => g.lines.length);
}

export function orderedDenomination(ordered, product, current, allowed) {
  const key = baseProductKey(product);
  const group = (Array.isArray(ordered) ? ordered : [])
    .find((g) => g && baseProductKey(g.product) === key);
  const ok = Array.isArray(allowed) && allowed.length
    ? (w) => allowed.some((x) => Number(x) === w)
    : () => true;
  const lines = (group && Array.isArray(group.lines) ? group.lines : [])
    .map((l) => ({ weight: Number(l && l.weight), qty: Number(l && l.qty) }))
    .filter((l) => l.weight > 0 && l.qty > 0 && ok(l.weight));
  if (!lines.length) return current;
  // The plate they designed on is on the order — leave it alone. This is the common case for anyone
  // who designed on the weight they actually wanted, and it must not be "corrected" out from under
  // them just because some other rung carries a bigger number.
  if (lines.some((l) => l.weight === Number(current))) return current;
  let best = lines[0];
  for (const l of lines) {
    if (l.qty > best.qty || (l.qty === best.qty && l.weight > best.weight)) best = l;
  }
  return best.weight;
}
